import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import useAnnouncement from "./useAnnouncement";

const schema = yup.object().shape({
  title: yup.string().required("Title is required"),
  body: yup.string().required("Body is required"),
  scheduledDate: yup.date().required("Scheduled date is required"),
  targetType: yup
    .string()
    .oneOf(["all", "users", "admins"])
    .required("Target type is required"),
  deliveryMethods: yup.object({
    useFcm: yup.boolean(),
  }),
  isSent: yup.boolean(),
});

function CreateAnnouncements({
  editingAnnouncement = null,
  onSuccess = null,
  onHandleCancel,
}) {
  const {
    createAnnouncement,
    updateAnnouncement,
    isCreatingAnnouncement,
    isUpdatingAnnouncement,
  } = useAnnouncement();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      title: "",
      body: "",
      scheduledDate: "",
      targetType: "all",
      deliveryMethods: { useFcm: true },
      isSent: false,
    },
  });

  // Populate form when editing
  useEffect(() => {
    if (editingAnnouncement) {
      setValue("title", editingAnnouncement.title || "");
      setValue("body", editingAnnouncement.body || "");
      setValue(
        "scheduledDate",
        editingAnnouncement.scheduledDate
          ? new Date(editingAnnouncement.scheduledDate)
              .toISOString()
              .slice(0, 16)
          : ""
      );
      setValue("targetType", editingAnnouncement.targetType || "all");
      setValue(
        "deliveryMethods.useFcm",
        editingAnnouncement.deliveryMethods?.useFcm ?? true
      );
      setValue("isSent", editingAnnouncement.isSent || false);
    }
  }, [editingAnnouncement, setValue]);

  const onSubmit = (data) => {
    // Format the data for the API
    // console.log("announcements data: ", data);
    const announcementData = {
      title: data.title,
      body: data.body,
      scheduledDate: data.scheduledDate,
      targetType: data.targetType,
      deliveryMethods: data.deliveryMethods,
      isSent: data.isSent,
    };

    if (editingAnnouncement) {
      updateAnnouncement({
        announcementId: editingAnnouncement._id,
        announcementData,
      });
    } else {
      createAnnouncement(announcementData);
    }

    if (onSuccess) {
      onSuccess();
    }
    reset();
  };

  const isLoading = isCreatingAnnouncement || isUpdatingAnnouncement;

  return (
    <form
      className="space-y-4 bg-gray-50 p-4 rounded-lg border"
      onSubmit={handleSubmit(onSubmit)}
    >
      <h3 className="text-lg font-normal mb-4">
        {editingAnnouncement ? "Edit Announcement" : "Create Announcement"}
      </h3>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Title
        </label>
        <input
          type="text"
          {...register("title")}
          className={`w-full border rounded p-2 ${
            errors.title ? "border-red-500" : "border-gray-300"
          }`}
          placeholder="Enter announcement title"
        />
        {errors.title && (
          <p className="text-red-500 text-xs mt-1">{errors.title.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Body
        </label>
        <textarea
          {...register("body")}
          className={`w-full border rounded p-2 ${
            errors.body ? "border-red-500" : "border-gray-300"
          }`}
          placeholder="Enter announcement body"
          rows={4}
        />
        {errors.body && (
          <p className="text-red-500 text-xs mt-1">{errors.body.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Scheduled Date
        </label>
        <input
          type="datetime-local"
          {...register("scheduledDate")}
          className={`w-full border rounded p-2 ${
            errors.scheduledDate ? "border-red-500" : "border-gray-300"
          }`}
        />
        {errors.scheduledDate && (
          <p className="text-red-500 text-xs mt-1">
            {errors.scheduledDate.message}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Target Type
        </label>
        <select
          {...register("targetType")}
          className={`w-full border rounded p-2 ${
            errors.targetType ? "border-red-500" : "border-gray-300"
          }`}
        >
          <option value="all">All Users</option>
          <option value="users">Users Only</option>
          <option value="admins">Admins Only</option>
        </select>
        {errors.targetType && (
          <p className="text-red-500 text-xs mt-1">
            {errors.targetType.message}
          </p>
        )}
      </div>

      <div className="flex items-center gap-4">
        <Controller
          name="deliveryMethods.useFcm"
          control={control}
          render={({ field }) => (
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">Use FCM</span>
            </label>
          )}
        />
      </div>

      <div className="flex items-center gap-4">
        <Controller
          name="isSent"
          control={control}
          render={({ field }) => (
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={field.value}
                disabled
                className="h-4 w-4 text-blue-600 border-gray-300 rounded cursor-not-allowed"
                readOnly
              />
              <span className="text-sm text-gray-700">Is Sent (auto)</span>
            </label>
          )}
        />
      </div>

      <div className="flex gap-4 pt-4">
        <button
          type="submit"
          disabled={isLoading}
          className={`px-4 py-2 rounded transition-colors ${
            isLoading
              ? "bg-gray-400 text-gray-200 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700 text-white"
          }`}
        >
          {isLoading
            ? editingAnnouncement
              ? "Updating..."
              : "Creating..."
            : editingAnnouncement
            ? "Update Announcement"
            : "Create Announcement"}
        </button>
        <button
          type="button"
          onClick={() => onHandleCancel()}
          className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default CreateAnnouncements;
